import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Info,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

export default function AdminAnnouncements() {
  const { authAxios } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal'
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await authAxios.get('/announcements');
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const resetForm = () => {
    setFormData({ title: '', content: '', priority: 'normal' });
    setEditingAnnouncement(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingAnnouncement) {
        await authAxios.put(`/announcements/${editingAnnouncement.id}`, formData);
        toast.success('Announcement updated!');
      } else {
        await authAxios.post('/announcements', formData);
        toast.success('Announcement created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await authAxios.put(`/announcements/${announcement.id}`, {
        is_active: !announcement.is_active
      });
      toast.success(`Announcement ${announcement.is_active ? 'hidden' : 'published'}!`);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDelete = async (announcement) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }
    try {
      await authAxios.delete(`/announcements/${announcement.id}`);
      toast.success('Announcement deleted!');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="w-5 h-5 text-rose-500" />;
      default:
        return <Info className="w-5 h-5 text-emerald-500" />;
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-rose-100 text-rose-800 border-rose-200',
      normal: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      low: 'bg-stone-100 text-stone-600 border-stone-200'
    };
    return styles[priority] || styles.normal;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-announcements">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Announcements</h1>
          <p className="text-stone-500 mt-1">Post updates for your team</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={openCreateDialog}
              className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20"
              data-testid="create-announcement-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="announcement-title-input"
                  className="bg-stone-50 border-stone-200"
                  placeholder="Enter announcement title..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={5}
                  data-testid="announcement-content-input"
                  className="bg-stone-50 border-stone-200 resize-none"
                  placeholder="Write your announcement..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger data-testid="announcement-priority-select" className="bg-stone-50 border-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High (Urgent)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  data-testid="save-announcement-btn"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white w-full"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingAnnouncement ? 'Update Announcement' : 'Post Announcement'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : announcements.length === 0 ? (
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardContent className="p-12 text-center">
            <Megaphone className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-600">No announcements yet</h3>
            <p className="text-stone-400 mt-1">Create your first announcement to keep your team informed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <Card 
              key={announcement.id} 
              className={`border-0 shadow-lg shadow-stone-200/50 bg-white transition-all ${
                !announcement.is_active ? 'opacity-60' : ''
              } ${announcement.priority === 'high' ? 'ring-2 ring-rose-200' : ''}`}
              data-testid={`announcement-card-${announcement.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    announcement.priority === 'high' ? 'bg-rose-100' : 'bg-emerald-100'
                  }`}>
                    {getPriorityIcon(announcement.priority)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-stone-900 font-heading">{announcement.title}</h3>
                      <Badge variant="outline" className={getPriorityBadge(announcement.priority)}>
                        {announcement.priority}
                      </Badge>
                      {!announcement.is_active && (
                        <Badge variant="outline" className="bg-stone-100 text-stone-500 border-stone-200">
                          Hidden
                        </Badge>
                      )}
                    </div>
                    <p className="text-stone-600 whitespace-pre-wrap leading-relaxed mb-4">{announcement.content}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-stone-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {fmtTz(announcement.created_at, 'MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 mr-4">
                          <Switch
                            checked={announcement.is_active}
                            onCheckedChange={() => handleToggleActive(announcement)}
                            data-testid={`toggle-announcement-${announcement.id}`}
                          />
                          <span className="text-sm text-stone-500">
                            {announcement.is_active ? 'Published' : 'Hidden'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(announcement)}
                          data-testid={`edit-announcement-${announcement.id}`}
                          className="hover:bg-emerald-50"
                        >
                          <Pencil className="w-4 h-4 text-stone-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(announcement)}
                          data-testid={`delete-announcement-${announcement.id}`}
                          className="hover:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
